import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * Button — the primary + secondary button from the DESIGN.md mocks.
 * "primary" uses the orange gradient + orange drop shadow; "secondary"
 * is the slate200-bordered white pill used next to it.
 */
const button = cva(
  'inline-flex items-center justify-center gap-2 rounded-full font-sans font-semibold transition-[filter,transform,background] disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40',
  {
    variants: {
      variant: {
        primary: 'or-gradient-btn',
        secondary: 'border border-or-slate-200 bg-white text-or-slate-700 hover:bg-or-slate-50',
        ghost: 'bg-transparent text-or-slate-700 hover:bg-or-slate-100',
        danger: 'border border-or-rose-100 bg-white text-or-rose-700 hover:bg-or-rose-100',
        // `success` is the "you finished it!" CTA — flat or-green-700 with
        // a soft drop shadow + subtle white inset highlight to match the
        // raised feel of the primary orange gradient.
        success:
          'bg-or-green-700 text-white shadow-or-card hover:brightness-[1.05] disabled:hover:brightness-100',
      },
      size: {
        sm: 'h-8 px-3 text-[12px]',
        md: 'h-10 px-4 text-[13px]',
        lg: 'h-11 px-5 text-[14px]',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, type = 'button', ...rest },
  ref,
) {
  return (
    <button ref={ref} type={type} className={cn(button({ variant, size }), className)} {...rest} />
  );
});
